import React, { memo, useMemo, useEffect, useState, useCallback } from 'react';
import { Popper, PopperAnchor, usePopperAnchor } from '@core/components/Popper';
import { GCCalendarOverlayBloc } from './GCCalendarOverlayBloc';
import { useObservable } from '@core/utils/hooks/rxjs';
import { CalendarView, Calendar, CalendarHandle } from '@modules/common/Calendar';
import { UserSocialId } from '@business/entities/account';
import { EventPopper } from './EventPopper';
import { AddCalendarEvent } from './AddCalendarEvent';
import { Moment } from 'moment';
import { RecursivePartial, useLazyEffect } from '@core/utils/react';
import { tap, switchMap } from 'rxjs/operators';
import { DatesSetArg } from '@fullcalendar/core';
import { Card, CardBody } from 'reactstrap';
import { toMoment } from '@core/utils/time';
import useWindowDimensions from '@core/utils/hooks/dom/window';
import { of } from 'rxjs';
import { openEditEventModal } from './EditEventModal';
import { Calendar as AntCalendar } from 'antd';
import type { Moment as TMoment } from 'moment';
import momentGenerateConfig from 'rc-picker/es/generate/moment';
import { GCCalendarEventObjectType, GCCalendarEventResponse, GCCalendarEventStatus } from '@business/entities/gc_calendar';
import { GCModulesCalendarBloc } from '@modules/common/GCModulesCalendar/GCModulesCalendarBloc';
import { OrgAddressResponse, OrgId } from '@business/entities/organization';
import { CalendarService, CommonService } from '@business/services';
import { useDidMount } from '@core/utils/hooks/react';
import { Formatter } from '@core/utils/formatter';

const MomentCalendar = AntCalendar.generateCalendar<TMoment>(momentGenerateConfig);

const LOCAL_COLORS = [
  '#319cf4',
  '#9e4171',
  '#10759c',
  '#8e7c3c',
  '#4f3f84',
  '#d52322',
  '#399121',
  '#3c17e5',
  '#a58a3b',
  '#b84c51',
  '#7330e6',
  '#16bc8c',
];

const GOOGLE_SYNCED_COLORS = [
  '#4285F4',
  '#EA4335',
  '#FBBC04',
  '#34A853',
  '#FF6D00',
  '#46BDC6',
  '#7B1FA2',
  '#E91E63',
  '#00BCD4',
  '#8BC34A',
  '#FF9800',
  '#795548',
];

export interface GCCustomCalendarProps {
  bloc: GCCalendarOverlayBloc | GCModulesCalendarBloc;
  socials?: UserSocialId[];
  selectedUsers?: any[];
  emailSelector: React.ReactNode;
  selectedUserEmails?: string[];
  initialView?: CalendarView;
  objectType?: GCCalendarEventObjectType;
  objectId?: string;
  orgId?: OrgId;
  selectedGuestEmails?: string[];
  forceLoadView?: boolean;
  objectName?: string;
  objectAddress?: OrgAddressResponse | null;
  originalObjectType?: GCCalendarEventObjectType;
  originalObjectId?: string;
}

export const GCCustomCalendar = memo((props: GCCustomCalendarProps) => {
  const {
    bloc,
    selectedUsers = [],
    emailSelector,
    selectedUserEmails = [],
    initialView = CalendarView.month,
    objectType,
    objectId,
    orgId,
    selectedGuestEmails = [],
    forceLoadView = false,
    objectName,
    objectAddress,
    originalObjectId,
    originalObjectType,
  } = props;
  const events = useObservable(bloc.events$, []);
  const [currentEvent, setCurrentEvent] = useState<GCCalendarEventResponse | null>();
  const addEventAnchor = usePopperAnchor();
  const editEventAnchor = usePopperAnchor();
  const [selectedDate, setSelectedDate] = useState<Moment | null>(null);
  const [dateRange, setDateRanges] = useState<DatesSetArg | null>(null);
  const calendarRef = React.useRef<CalendarHandle | null>(null);
  const { height } = useWindowDimensions();
  const [autoRefresh, setAutoreresh] = useState(0);
  const [isMovedToNow, setIsMovedToNow] = useState(false);
  const users = useObservable(bloc.users$, []);
  const teams = useObservable(bloc.teams$, []);

  const onAllEventsReload = useCallback(() => {
    if (objectId && objectType) {
      const sTime = dateRange ? dateRange.start.valueOf() / 1000 - 30 * 60 : undefined;
      const eTime = dateRange ? dateRange.end.valueOf() / 1000 + 30 * 60 : undefined;
      bloc.onAllEventsReload(objectType, objectId, orgId, sTime, eTime);
    }
  }, [bloc, objectId, objectType, orgId, dateRange]);

  const onSave = useCallback(
    (data: RecursivePartial<GCCalendarEventResponse>, eventId?: string) => {
      return of(null).pipe(
        switchMap(() => {
          if (!eventId) {
            return bloc.addEvent(data);
          } else {
            return bloc.editEvent(data, eventId);
          }
        }),
        tap(() => {
          if (eventId) {
            editEventAnchor.onClose();
          } else {
            addEventAnchor.onClose();
          }
        }),
        tap(() => setAutoreresh(autoRefresh + 1)),
        tap(() => {
          onAllEventsReload();
        }),
      );
    },
    [addEventAnchor, autoRefresh, bloc, editEventAnchor, onAllEventsReload],
  );

  const selectedUserIds = useMemo(() => {
    return selectedUsers.map(u => u.id);
  }, [selectedUsers]);

  useLazyEffect(
    () => {
      if (autoRefresh && dateRange && selectedUserIds.length > 0) {
        bloc
          .fetchEvents({
            objectType,
            objectId,
            orgId,
            userIds: selectedUserIds,
            startTime: dateRange.start.valueOf() / 1000 - 30 * 60,
            endTime: dateRange.end.valueOf() / 1000 + 30 * 60,
          })
          .subscribe();
      }
    },
    [autoRefresh],
    5000,
  );

  useEffect(() => {
    if (dateRange && selectedUserIds.length > 0) {
      bloc
        .fetchEvents({
          objectType,
          objectId,
          orgId,
          userIds: selectedUserIds,
          startTime: dateRange.start.valueOf() / 1000 - 30 * 60,
          endTime: dateRange.end.valueOf() / 1000 + 30 * 60,
        })
        .subscribe();
    }
  }, [bloc, dateRange, objectId, objectType, orgId, selectedUserIds]);

  const calendarResources = useMemo(() => {
    // When viewing object-specific calendar (deal/project/etc), hide resource names
    const isObjectView = !!objectId && !!objectType;
    return selectedUsers.map(user => ({
      id: user.id,
      title: isObjectView ? '' : CommonService.getFullName(user),
      type: 'user' as const,
      email: user.email,
    }));
  }, [selectedUsers, objectId, objectType]);

  const eventItems = useMemo(() => {
    const items: any[] = [];
    const isObjectView = !!objectId && !!objectType;

    events.forEach(e => {
      const allParticipantUserIds = e.participants.map(p => p.userId).filter((userId): userId is string => !!userId);
      const participantUserIds = allParticipantUserIds.filter(userId => selectedUserIds.includes(userId));
      const createdById = e.createdBy?.id;
      const isCreatorSelected = createdById && selectedUserIds.includes(createdById);

      const isGoogleSynced = !!e.isSynced;
      const statusIcon = CalendarService.getEventStatusIcon(e.status);
      // const syncIcon = isGoogleSynced ? '📅 ' : '';
      const syncIcon = isGoogleSynced ? '' : '';

      // When viewing object-specific calendar (deal/project/etc), show ALL events
      // regardless of participant status
      if (isObjectView) {
        // Show event for all selected users in resource view
        selectedUserIds.forEach(userId => {
          items.push({
            id: `${e.id}-${userId}`,
            title: `${syncIcon}${statusIcon}${e.name} ${e.associatedValue ? `(${Formatter.formatCurrency(e.associatedValue, 'CAD')})` : ''}`,
            start: e.startTime,
            end: e.endTime,
            item: e,
            resourceId: userId,
            type: 'event',
            isGoogleSynced,
            dbEventId: e.id,
            eventStatus: e.status,
            strikeThrough: e.strikeThrough,
            color: e.color,
            reminders: e.reminders || [],
          });
        });
      } else {
        // When NOT in object view, only show events where selected user is participant/creator
        if (participantUserIds.length > 0) {
          participantUserIds.forEach(userId => {
            items.push({
              id: `${e.id}-${userId}`,
              title: `${syncIcon}${statusIcon}${e.name} ${e.associatedValue ? `(${Formatter.formatCurrency(e.associatedValue, 'CAD')})` : ''}`,
              start: e.startTime,
              end: e.endTime,
              item: e,
              resourceId: userId,
              type: 'event',
              isGoogleSynced,
              dbEventId: e.id,
              eventStatus: e.status,
              strikeThrough: e.strikeThrough,
              color: e.color,
              reminders: e.reminders || [],
            });
          });
        } else if (isCreatorSelected) {
          items.push({
            id: e.id,
            title: `${syncIcon}${statusIcon}${e.name} ${e.associatedValue ? `(${Formatter.formatCurrency(e.associatedValue, 'CAD')})` : ''}`,
            start: e.startTime,
            end: e.endTime,
            item: e,
            resourceId: createdById,
            type: 'event',
            isGoogleSynced,
            dbEventId: e.id,
            eventStatus: e.status,
            strikeThrough: e.strikeThrough,
            color: e.color,
            reminders: e.reminders || [],
          });
        }
      }
    });

    return items;
  }, [events, selectedUserIds, objectId, objectType]);

  const smallCalenderValue = useMemo(() => {
    if (dateRange) {
      if (dateRange.view.type === 'dayGridMonth') {
        const m = toMoment(dateRange.start);
        if (m.date() == 1) {
          return m.toDate();
        } else {
          return m.add(1, 'month').startOf('month').toDate();
        }
      } else {
        return dateRange.start;
      }
    }
    return null;
  }, [dateRange]);

  const eventAnchor = usePopperAnchor();

  useDidMount(() => {
    if (forceLoadView && calendarRef.current && initialView) {
      const calendar = calendarRef.current.calendar();
      setTimeout(() => {
        calendar.changeView(initialView);
      }, 2000);
    }
  });

  useLazyEffect(() => {
    if (dateRange && dateRange.view.type !== 'dayGridMonth' && !isMovedToNow) {
      setIsMovedToNow(true);
      calendarRef.current?.calendar()?.scrollToTime(toMoment().format('HH:mm:ss'));
    }
  }, [dateRange, isMovedToNow]);

  const calendarColors = useMemo(() => {
    const localColors: Record<string, string> = {};
    const googleSyncedColors: Record<string, string> = {};

    calendarResources.forEach((resource, i) => {
      localColors[resource.id] = LOCAL_COLORS[i % LOCAL_COLORS.length];
      googleSyncedColors[resource.id] = GOOGLE_SYNCED_COLORS[i % GOOGLE_SYNCED_COLORS.length];
    });

    return { localColors, googleSyncedColors };
  }, [calendarResources]);

  const onDeleteEvent = useCallback(
    (eventId: string) => {
      return bloc
        .deleteEvent(eventId)
        .pipe(
          tap(() => setAutoreresh(r => r + 1)),
          tap(() => onAllEventsReload()),
        )
        .subscribe();
    },
    [bloc, onAllEventsReload],
  );

  const onAddToGoogleCalendar = useCallback(
    (eventId: string) => {
      return bloc
        .addEventToGoogleCalendar(eventId)
        .pipe(
          tap(event => {
            setCurrentEvent(event);
          }),
        )
        .subscribe();
    },
    [bloc],
  );

  const onEventDrop = useCallback(
    (eventId: string, event: GCCalendarEventResponse, newStart: number, newEnd?: number, newResourceId?: string) => {
      const updatedData: any = {
        startTime: newStart,
        endTime: newEnd || event.endTime,
      };

      if (newResourceId) {
        updatedData.newResourceId = newResourceId;
      }

      return bloc
        .dragEvent(eventId, updatedData)
        .pipe(
          tap(() => {
            setAutoreresh(r => r + 1);
            onAllEventsReload();
          }),
        )
        .subscribe();
    },
    [bloc, onAllEventsReload],
  );

  return (
    <Card>
      <CardBody className="d-block d-md-flex">
        <div className="calendar-sidebar mr-4 " style={{ width: 250, marginTop: 50 }}>
          <div className="mb-4">
            <MomentCalendar
              mode="month"
              fullscreen={false}
              value={toMoment(smallCalenderValue)}
              onSelect={(v, s) => calendarRef.current?.calendar()?.gotoDate(v.toDate())}
            />
          </div>
          {emailSelector}
        </div>
        <div style={{ height: dateRange?.view.type === 'dayGridMonth' ? 'calc(100vh - 200px)' : undefined, width: '100%' }}>
          <Calendar
            enableDragAndDrop={true}
            initialView={initialView}
            handlesRef={calendarRef}
            stickyHeaderDates
            contentHeight={undefined}
            height={dateRange?.view.type === 'resourceTimeGridDay' || dateRange?.view.type === 'timeGridWeek' ? height - 175 : '100%'}
            resources={calendarResources.map(resource => ({
              id: resource.id,
              title: resource.title,
            }))}
            resourceLabelContent={arg => {
              // Return empty string when in object view (title is already empty)
              // This prevents FullCalendar from showing the resource ID
              return arg.resource.title || '';
            }}
            eventClassNames={arg => {
              const event = arg.event;
              const isGoogleSynced = event.extendedProps?.isGoogleSynced;
              return isGoogleSynced ? 'google-synced-event' : 'local-event';
            }}
            eventDidMount={info => {
              const event = info.event;
              const resourceId = event.getResources()[0]?.id || event.extendedProps?.resourceId;
              const isGoogleSynced = event.extendedProps?.isGoogleSynced;
              const eventStatus = event.extendedProps?.eventStatus;

              if (eventStatus !== 'declined' && event.extendedProps?.color) {
                info.el.style.backgroundColor = event.extendedProps.color;
                info.el.style.borderColor = event.extendedProps.color;
                return;
              }

              if (eventStatus === 'declined') {
                info.el.style.backgroundColor = '#999999';
                info.el.style.borderColor = '#999999';
                info.el.style.opacity = '0.6';
              } else if (resourceId) {
                const color = isGoogleSynced ? calendarColors.googleSyncedColors[resourceId] : calendarColors.localColors[resourceId];

                if (color) {
                  info.el.style.backgroundColor = color;
                  info.el.style.borderColor = color;
                }
              }
            }}
            slotLabelFormat={{
              hour: 'numeric',
              minute: '2-digit',
              omitZeroMinute: true,
              meridiem: 'short',
              hour12: true,
            }}
            dayMaxEventRows={true}
            views={{
              timeGrid: {
                dayMaxEventRows: 6,
              },
            }}
            nowIndicator
            types={[CalendarView.month, CalendarView.week, CalendarView.resource]}
            datesSet={info => setDateRanges(info)}
            displayEventTime
            eventDisplay="list-item"
            selectable
            select={info => {
              setSelectedDate(toMoment(info.start));
              addEventAnchor.onClick({ ...info.jsEvent, currentTarget: info.jsEvent?.target } as any);
            }}
            onDayClick={(d, event) => {
              setSelectedDate(d);
              addEventAnchor.onClick({ ...event.jsEvent, currentTarget: event.dayEl } as any);
            }}
            onEventClick={(id, item: GCCalendarEventResponse, event) => {
              setCurrentEvent(item);
              eventAnchor.onClick({ ...event.jsEvent, currentTarget: event.el } as any);
            }}
            onEventChange={(id, item: GCCalendarEventResponse, start, end, newResourceId) => {
              onEventDrop(id, item, start, end, newResourceId);
            }}
            events={eventItems}
          />
        </div>
        {!!currentEvent && (
          <EventPopper
            anchor={eventAnchor}
            event={currentEvent}
            onAddToGoogle={(eventId: string) => onAddToGoogleCalendar(eventId)}
            onEdit={event => {
              openEditEventModal({
                event,
                date: null,
                onSave: (data, eventId) => onSave(data, eventId),
                users,
              });
            }}
            onDelete={id => onDeleteEvent(id)}
            isMyEvent={true}
          />
        )}
        <AddCalendarEvent
          anchor={addEventAnchor}
          date={selectedDate}
          onSave={onSave}
          selectedUserEmails={selectedUserEmails}
          selectedGuestEmails={selectedGuestEmails}
          users={users}
          objectType={originalObjectType}
          objectId={originalObjectId}
          // objectType={objectType}
          // objectId={objectId}
          orgId={orgId}
          objectName={objectName}
          objectAddress={objectAddress}
        />
      </CardBody>
    </Card>
  );
});

export interface GCCustomCalendarPropsWithAnchor extends GCCustomCalendarProps {
  anchor: PopperAnchor<HTMLElement>;
}
export const GCCalendarPopper = memo((props: GCCustomCalendarPropsWithAnchor) => {
  const { anchor, ...rest } = props;
  return (
    <Popper anchor={anchor} placement="bottom" autoClose="outside" aboveModal>
      <div className="poper-modal">
        <GCCustomCalendar {...rest} />
      </div>
    </Popper>
  );
});
